import type { LiveConnection, LiveProcess } from '../api/deviceLive'

/**
 * Socotelile pe care le facea panoul Live inainte sa deseneze: `GroupProcesses`,
 * `GroupConnections`, `prettysize` si `ipIsInCidr` din
 * `public/js/easeedesk/easeedesk.js` si `public/js/general.js`.
 *
 * Mutate ca functii curate, ca sa se poata citi si proba fara ecran.
 */

/** Grupata cu conexiunile numarate. */
export interface GroupedConnection extends LiveConnection {
  total_connections: number
}

/**
 * `prettysize` din `general.js:124`.
 *
 * Nu e `convertBytes`: alta scara de nume (`kB`, nu `KB`), o singura zecimala,
 * si taie `.0`. Panoul vechi il foloseste pe asta pentru memoria proceselor si
 * pentru citirile de disc, deci aici e acelasi.
 */
export function prettySize(size: number, places = 1): string {
  const sizes = ['Bytes', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB']
  let out = ''

  for (let id = 0; id < sizes.length; ++id) {
    const step = Math.pow(1024, id)
    if (size >= step) {
      let fixed = (size / step).toFixed(places)
      if (fixed.endsWith('.0')) {
        fixed = fixed.slice(0, -2)
      }
      out = `${fixed} ${sizes[id]}`
    }
  }

  /* Zero se scrie intotdeauna in octeti, ca in portal. */
  return out === '' ? '0 Bytes' : out
}

/**
 * `GroupProcesses` (`easeedesk.js:7052`).
 *
 * Masina trimite fiecare instanta a unui program ca proces separat, numite
 * `chrome#1`, `chrome#2`. Cine se uita la ecran vrea sa vada "chrome" o data,
 * cu tot ce mananca el la un loc - altfel un browser cu douazeci de file umple
 * lista si ascunde ce e in spatele lui.
 *
 * Sortarea e descrescatoare dupa incarcarea de procesor, ca in portal.
 */
export function groupProcesses(processes: LiveProcess[]): LiveProcess[] {
  const merged = new Map<string, LiveProcess>()
  const single: LiveProcess[] = []

  for (const process of processes) {
    if (typeof process?.name !== 'string' || !process.name.includes('#')) {
      continue
    }
    const name = process.name.split('#')[0] ?? process.name
    const seen = merged.get(name)
    if (seen) {
      seen.memory += process.memory
      seen.cpuLoad += process.cpuLoad
    } else {
      merged.set(name, { ...process, name })
    }
  }

  for (const process of processes) {
    if (typeof process?.name !== 'string' || process.name.includes('#')) {
      continue
    }
    const seen = merged.get(process.name)
    if (seen) {
      seen.memory += process.memory
      seen.cpuLoad += process.cpuLoad
    } else {
      single.push(process)
    }
  }

  return [...single, ...merged.values()].sort((a, b) => b.cpuLoad - a.cpuLoad)
}

/** `GroupConnections` (`easeedesk.js:7089`): acelasi "ip:port", numarat. */
export function groupConnections(connections: LiveConnection[]): GroupedConnection[] {
  const grouped = new Map<string, GroupedConnection>()

  for (const connection of connections) {
    if (typeof connection?.address !== 'string') {
      continue
    }
    const seen = grouped.get(connection.address)
    if (seen) {
      seen.total_connections += 1
    } else {
      grouped.set(connection.address, { ...connection, total_connections: 1 })
    }
  }

  return [...grouped.values()]
}

/** `ipIsInCidr` + `IPnumber` + `IPmask` (`general.js:2994`). Doar IPv4. */
export function ipInCidr(ip: string, cidr: string): boolean {
  const [network, bits] = cidr.split('/')
  const size = Number(bits)
  const address = ipNumber(ip)
  const base = ipNumber(network ?? '')

  if (address === null || base === null || !Number.isFinite(size)) {
    return false
  }

  const mask = size === 0 ? 0 : -1 << (32 - size)

  return (address & mask) === base
}

function ipNumber(ip: string): number | null {
  const parts = /^(\d+)\.(\d+)\.(\d+)\.(\d+)$/.exec(ip)
  if (parts === null) {
    return null
  }

  return (
    ((Number(parts[1]) << 24) +
      (Number(parts[2]) << 16) +
      (Number(parts[3]) << 8) +
      Number(parts[4])) |
    0
  )
}
